import { apiRequestSecureGet, apiRequestSecuredPatch, apiRequestSecuredPost, apiRequestSecuredPut } from "../../lib/common/api";
import { TEmployeeRole } from "../../lib/types/employee_roles";
import { TGenericApiResponse } from "../../lib/types/general";
import { TUserTypes } from "../../lib/types/users";

export type TReportStats = {
    unhandled: number;
    underReview: number;
    appealed: number;
};

export type TReportState = "draft" | "under_review" | "archived" | "accepted" | "rejected" | "appeal";

export type TUsersStats = {
    labs: { disabled: number; enabled: number };

    techs: { disabled: number; enabled: number };
};

export type TReportedUserRow = {
    creation_date: string;
    reason: string;
    reported_name: string;
    reported_user_id: string;
    reporter_name: string;
    reporter_user_id: string;
    state: TReportState;
    user_type: TUserTypes;
    reported_enabled: boolean;
};

export type TServicesStatus = TGenericApiResponse<{
    cacheToGo: {
        active: boolean;
        errorMessage?: string;
    };
}>;

type TAPIReportedUserReponse = {
    totalRows: number;
    users: TReportedUserRow[];
};

const URLS = {
    reportStats: "reports/stats",
    userStats: "/users/stats",
    reportedUsers: "reports/get/",
    enableDisable: "users/enable-disable",
    updateReportState: "reports/update-state",
    allPaymentRecords: "payment_record/all/",
    retryPayment: "payment_record/manualpaymentintent",
    subscriptionsList: "payment/all-subscriptions/",
    editCredit: "payment/update-credit",
    getAllEmployeeRoles: "employee-roles/all/",
    updateEmployeeRole: "employee-roles/update/",
    addEmployeeRole: "employee-roles/add/",
    addBusinessType: "business-types/add",
    updateBusinessType: "business-types/update/",
    servicesStatus: "backoffice/systemstatus",
};

export const getStats = async (userTokenId: string) => {
    return apiRequestSecureGet<{ data: TReportStats }>(URLS.reportStats, userTokenId);
};

export const getUserStats = async (tokenId: string) => {
    return apiRequestSecureGet<{ data: TUsersStats }>(URLS.userStats, tokenId);
};

export const getServicesStatus = async (tokenId: string) => {
    return apiRequestSecureGet<TServicesStatus>(URLS.servicesStatus, tokenId);
};

export type TReportedUsersFilters = {
    filterStates: TReportState[];
    filterUserNames: string[];
};
export const getReportedUsers = async (page: number, tokenId: string, filters: TReportedUsersFilters) => {
    return apiRequestSecuredPost<{ data: TAPIReportedUserReponse }>(`${URLS.reportedUsers}${page}`, tokenId, filters);
};

export const enableOrDisableUser = async (userId: string, enabled: boolean, tokenId: string) => {
    return apiRequestSecuredPost(URLS.enableDisable, tokenId, {
        userId,
        enabled,
    });
};

export const updateReportState = async (state: TReportState, reported_user_id: string, tokenId: string) => {
    return apiRequestSecuredPut(URLS.updateReportState, tokenId, {
        state,
        reported_user_id,
    });
};

export type TPaymentState = "success" | "failed";

export type TPaymentMethod = {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
};
export type TPaymentRow = {
    id: number;
    user_id: number;
    name: string;
    amount: string;
    payment_method: TPaymentMethod;
    creation_date: string;
    concept: string;
    state: TPaymentState;
};

export type TPaymentRecordResponse = {
    totalRecordsCount: number;
    records: TPaymentRow[];
};
export const getPaymentRecords = async (pageNumberParam: number, tokenId: string) => {
    return apiRequestSecureGet<{ data: TPaymentRecordResponse }>(`${URLS.allPaymentRecords}${pageNumberParam}`, tokenId);
};

export const retryPayment = async (userId: number, tokenId: string) => {
    return apiRequestSecureGet(`${URLS.retryPayment}?id=${userId}`, tokenId);
};

export type TEnabledDisabledStates = "disabled" | "enabled" | "all";
export type TSubscriptionFilterProps = {
    name: string;
    enabled: TEnabledDisabledStates;
};

export type TSubscriptionRow = {
    id: number;
    userid: number;
    name: string;
    credits: number;
    payment_method: TPaymentMethod;
    update_date: string;
    enabled: number;
};
export type TSubscriptionResponse = {
    totalSubscriptionsCount: number;
    subscriptionsData: TSubscriptionRow[];
};
export const getAllSubscriptions = async (page: number, tokenId: string, filterProps: TSubscriptionFilterProps) => {
    return await apiRequestSecuredPost<{ data: TSubscriptionResponse }>(`${URLS.subscriptionsList}${page}`, tokenId, filterProps);
};

export const editCreditsApi = async (tokenId: string, userid: number, credits: number) => {
    return await apiRequestSecuredPost<{ data: TSubscriptionResponse }>(`${URLS.editCredit}`, tokenId, {
        userId: userid,
        credits: credits,
    });
};

export type TEmployeeRolesFilters = {
    name: string;
};
export type TEmployeeRolesResults = {
    employeeRolesData: TEmployeeRole[];
    totalRolesCount: number;
};

export type TUpdateRole = {
    employee_role_id: number;
    skills_ids?: number[];
    role_name?: string;
    search_name?: string;
};

export type TAddEmployeeRole = {
    employee_role: string;
    skill_ids: number[];
};

export type TSearchSkillsAddRoleModal = {
    name: string;
};

export const getEmployeeRoles = async (page: number, tokenId: string, filterProps: TEmployeeRolesFilters) => {
    return await apiRequestSecuredPost<{
        data: TEmployeeRolesResults;
    }>(`${URLS.getAllEmployeeRoles}${page}`, tokenId, filterProps);
};

export const updateEmployeeRole = async (props: TUpdateRole, tokenId: string) => {
    const skillsObjects = props.skills_ids ? props.skills_ids.map((id) => ({ id })) : [];

    return await apiRequestSecuredPatch(`${URLS.updateEmployeeRole}${props.employee_role_id}`, tokenId, { skills: skillsObjects, name: props.role_name });
};

export const addEmployeeRole = async (inputData: TAddEmployeeRole, tokenId: string) => {
    return await apiRequestSecuredPost<{ inputData: TAddEmployeeRole }>(`${URLS.addEmployeeRole}`, tokenId, inputData);
};

//#region Business Types

export type TUpdateBusinessTypeData = {
    name: string;
    id: number;
};

export const addBusinessType = async (name: string, tokenId: string) => {
    return await apiRequestSecuredPost(URLS.addBusinessType, tokenId, { name });
};

export const updateBusinessType = async (props: TUpdateBusinessTypeData, tokenId: string) => {
    return await apiRequestSecuredPatch(`${URLS.updateBusinessType}${props.id}`, tokenId, { name: props.name });
};

//#endregion
